<template>
  <material-card
    :title="title"
    data-testId="material-card-title"
    color="dover-blue">
    <v-form
      ref="form"
      v-model="isValidationOk"
      lazy-validation>
      <v-container
        py-0>
        <v-layout
          wrap>
          <v-flex
            xs12
            md6
            lg6>
            <span
              data-testId="span-Dispenser"
              class="caption grey--text font-weight-light">
              {{ $t('Dispenser ({0})', [ $t('POS system settings')]) }}
            </span>
            <hr>
            <v-radio-group
              v-model.number="siteMapping.dispenserIndexOffset"
              :row="true"
              data-testId="radio-group">
              <v-radio
                :value="0"
                :label="`${ $t('Dispenser {0} has index {1}', ['1', '0']) }`"
                data-testId="radio-Dispenser-0" />
              <v-radio
                :value="1"
                :label="`${ $t('Dispenser {0} has index {1}', ['1', '1']) }`"
                data-testId="radio-Dispenser-1" />
            </v-radio-group>
          </v-flex>
          <v-flex
            xs12
            md6
            lg6>
            <span
              class="caption grey--text font-weight-light">
              {{ $t('Nozzle ({0})', [ $t('POS system settings') ]) }}
            </span>
            <hr>
            <v-radio-group
              v-model.number="siteMapping.nozzleIndexOffset"
              :row="true"
              data-testId="radio-group-2">
              <v-radio
                :value="0"
                :label="`${ $t('Nozzle {0} has index {1}', ['1', '0']) }`"
                data-testId="radio-Nozzle-0" />
              <v-radio
                :value="1"
                :label="`${ $t('Nozzle {0} has index {1}', ['1', '1']) }`"
                data-testId="radio-Nozzle-1" />
            </v-radio-group>
          </v-flex>
          <v-flex
            xs12
            md12
            lg12>
            <hr>
          </v-flex>
          <v-flex
            xs12
            md3
            lg3>
            <v-select
              v-model.number="siteMapping.numberOfDispensers"
              :items="dispenserList"
              :label="`${ $t('Total dispensers') }`"
              data-testId="select-Total-dispensers"
              item-text=""
              item-value=""
              class="dover-input"
              chips
              @change="onMaxDispensersChange()">
              <template
                slot="selection"
                slot-scope="data">
                <v-chip
                  :selected="data.selected"
                  :disabled="data.disabled"
                  :key="data.item"
                  data-testId="chip"
                  class="v-chip--select-multi "
                  @input="data.parent.selectItem(data.item)">
                  <v-avatar class="dover-avatar ">{{ data.item }}</v-avatar>
                  {{ $t('Dispensers') }}
                </v-chip>
              </template>
            </v-select>
          </v-flex>
          <v-flex
            xs12
            md3
            lg3>
            <v-select
              v-model.number="siteMapping.numberOfNozzles"
              :items="nozzleList"
              :label="`${ $t('Max nozzles / dispenser') }`"
              data-testId="select-Max-nozzles-dispenser"
              item-text=""
              item-value=""
              class="dover-input"
              chips
              @change="onMaxNozzlesChange()">
              <template
                slot="selection"
                slot-scope="data">
                <v-chip
                  :selected="data.selected"
                  :disabled="data.disabled"
                  :key="data.item"
                  data-testId="chip-2"
                  class="v-chip--select-multi "
                  @input="data.parent.selectItem(data.item)">
                  <v-avatar class="dover-avatar ">{{ data.item }}</v-avatar>
                  {{ $t('Nozzles') }}
                </v-chip>
              </template>
            </v-select>
          </v-flex>
          <v-flex
            xs12
            sm12
            class="py-2">
            <span
              data-testId="span-Dispenser"
              class="caption grey--text font-weight-bold">
              {{ $t('Dispenser') }}
            </span>
            <hr>
            <v-btn-toggle
              v-model.number="dispenserIndex"
              data-testId="btn-toggle">
              <v-btn
                v-for="disp in siteMapping.numberOfDispensers"
                :key="disp"
                :value="disp - 1"
                data-testId="btn-disp"
                text
                small
                color="dover-blue"
                @click="onDispenserChange(disp)">
                {{ disp }}
              </v-btn>
            </v-btn-toggle>
          </v-flex>
          <v-flex
            xs12
            md12>
            <v-data-table
              v-if="siteMapping.dispensers !== undefined && siteMapping.dispensers[dispenserIndex] !== undefined"
              :headers="headers"
              :items="siteMapping.dispensers[dispenserIndex].nozzles"
              :no-data-text="`${ Maglink.NoDataMessage(false) }`"
              :no-results-text="`${ Maglink.NoDataMessage(true) }`"
              data-testId="data-table-Maglink-NoDataMessage"
              hide-actions>
              <template
                slot="headerCell"
                slot-scope="{ header }">
                <span
                  data-testId="span-header-text"
                  class="dover-light-blue-color"
                  v-text="header.text" />
              </template>
              <template
                slot="items"
                slot-scope="{ item }">
                <td width="5%">{{ item.nozzleId }}</td>
                <td width="10%">
                  <v-text-field
                    v-if="item.enabled"
                    v-model="item.offset1"
                    :rules="rules.required.concat(rules.rangeOffset)"
                    :label="`${ $t('Offset') }`"
                    data-testId="text-field-Offset"
                    class="dover-input"/>
                </td>
                <td width="80%">
                  <v-btn-toggle
                    v-if="item.enabled"
                    v-model.number="item.tankNumber1"
                    data-testId="btn-toggle-2">
                    <v-btn
                      v-for="tank in tankList"
                      :key="tank"
                      :value="tank"
                      data-testId="btn-tank"
                      text
                      small
                      color="dover-blue">
                      {{ tank }}
                    </v-btn>
                  </v-btn-toggle>
                </td>
                <td width="5%">
                  <v-switch
                    v-model="item.enabled"
                    data-testId="switch"
                    class="dover-input text-xs-left" />
                </td>
              </template>
            </v-data-table>
          </v-flex>
          <v-flex
            xs12
            md12
            lg12
            text-xs-center>
            <v-btn
              :disabled="!isValidationOk || !Maglink.IsAdminUser()"
              data-testId="btn-Save-mapping"
              small
              class="mx-0 font-weight-light"
              color="dover-blue"
              @click="onSave()">
              {{ $t('Save mapping') }}
            </v-btn>
          </v-flex>
        </v-layout>
      </v-container>
    </v-form>
  </material-card>
</template>

<script>
import { i18n } from '@/i18n'
import Vue from 'vue'

export default {
  props: {
    title: {
      type: String,
      required: true
    }
  },
  data: () => ({
    isValidationOk: false,
    dispenserIndex: undefined,
    dispenserList: undefined,
    nozzleList: undefined,
    tankList: undefined,
    siteMapping: {},
    headers: [
      { sortable: false, text: i18n.t('Nozzle') },
      { sortable: false, text: i18n.t('Offset') },
      { sortable: false, text: i18n.t('Tank') },
      { sortable: false, text: i18n.t('Enabled') }
    ],
    rules: {
      required: [ v => Vue.prototype.Maglink.RulesRequired(v) ],
      rangeOffset: [ v => Vue.prototype.Maglink.RulesRange(v, -0.8, 4.0) ]
    }
  }),
  mounted () {
    this.dispenserList = []
    for (let index = 0; index <= 32; index++) {
      this.dispenserList.push(index)
    }

    this.nozzleList = []
    for (let index = 1; index <= 8; index++) {
      this.nozzleList.push(index)
    }

    this.Maglink.GetSiteMapping()
      .then(response => {
        this.siteMapping = response.data

        // get number of configured tanks
        this.Maglink.GetSettings('SiteInfo')
          .then(response => {
            let siteInfo = response.data
            this.tankList = []
            for (let index = 1; index <= siteInfo.tanksNumber; index++) {
              this.tankList.push(index)
            }
          })
          .catch(error => (this.snack('error', error.message)))
      }).catch(error => (this.snack('error', error.message)))
  },
  methods: {
    refreshDispenserCollection () {
      let dispensers = this.siteMapping.dispensers
      let currentTotalDispensers = dispensers.length

      // add new default record
      if (currentTotalDispensers < this.siteMapping.numberOfDispensers) {
        for (let dIndex = currentTotalDispensers + 1; dIndex <= this.siteMapping.numberOfDispensers; dIndex++) {
          let nozzles = []

          for (let nIndex = 1; nIndex <= this.siteMapping.numberOfNozzles; nIndex++) {
            nozzles.push(this.defaultNozzle(nIndex))
          }

          dispensers.push({
            dispenserId: dIndex,
            nozzles: nozzles
          })
        }
      } else {
        // remove last records
        dispensers.length = this.siteMapping.numberOfDispensers
      }
    },
    refreshNozzleCollection () {
      for (let dIndex = 1; dIndex <= this.siteMapping.numberOfDispensers; dIndex++) {
        let nozzles = this.siteMapping.dispensers[dIndex - 1].nozzles
        let currentTotalNozzles = nozzles.length

        if (currentTotalNozzles === this.siteMapping.numberOfNozzles) continue

        // add new default record
        if (currentTotalNozzles < this.siteMapping.numberOfNozzles) {
          for (let nIndex = currentTotalNozzles + 1; nIndex <= this.siteMapping.numberOfNozzles; nIndex++) {
            nozzles.push(this.defaultNozzle(nIndex))
          }
        } else {
          // remove last records
          nozzles.splice(this.siteMapping.numberOfNozzles, nozzles.length - this.siteMapping.numberOfNozzles)
        }
      }
    },
    defaultNozzle (dispenserIndex) {
      return { nozzleId: dispenserIndex,
        tankNumber1: 1,
        tankNumber2: 0,
        offset1: 0.0,
        offset2: 0.0,
        enabled: true
      }
    },
    onMaxNozzlesChange () {
      this.refreshNozzleCollection()
    },
    onMaxDispensersChange () {
      this.refreshDispenserCollection()
    },
    onDispenserChange (item) {
    },
    onSave (event) {
      if (this.$refs.form.validate()) {
        this.isValidationOk = true

        // must convert string to numbers
        // -----------------------------------------------------------------------
        let dispensers = this.siteMapping.dispensers

        for (let dispIndex = 0; dispIndex < dispensers.length; dispIndex++) {
          let nozzles = dispensers[dispIndex].nozzles

          for (let nozzleIndex = 0; nozzleIndex < nozzles.length; nozzleIndex++) {
            nozzles[nozzleIndex].offset1 = nozzles[nozzleIndex].offset1 * 1
            nozzles[nozzleIndex].offset2 = nozzles[nozzleIndex].offset2 * 1
          }
        }
        // -----------------------------------------------------------------------

        this.Maglink.SaveSiteMapping(this.siteMapping)
          .then(response => (this.snack('success', response.data['done'])))
          .catch(error => (this.snack('error', error.message)))

        this.$emit('settingsSaved')
      } else {
        this.isValidationOk = false
      }
    },
    snack (color, message) {
      message = this.title + ': ' + message
      this.$store.dispatch('showSnackbar', { color, message, show: true })
    }
  }
}
</script>
